/**
 * @fileoverview Which rows of a list are ticked, kept to the rows still offered.
 *
 * The keys passed in are the rows the reader can see and act on right now.
 * When a filter hides a ticked row, or a reload removes it, it drops out of
 * the selection: an action must never reach a row nobody can see.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * @param {string[]} keys the selectable rows, in list order
 */
export default function useRowSelection(keys) {
  const [selected, setSelected] = useState(() => new Set());
  const signature = keys.join("\n");
  const offered = useMemo(() => new Set(signature ? signature.split("\n") : []), [signature]);

  useEffect(() => {
    setSelected((current) => {
      const kept = [...current].filter((key) => offered.has(key));
      return kept.length === current.size ? current : new Set(kept);
    });
  }, [offered]);

  const toggle = useCallback(
    (key) =>
      setSelected((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );

  const allSelected = offered.size > 0 && [...offered].every((key) => selected.has(key));
  const toggleAll = useCallback(
    () => setSelected((current) => (offered.size > 0 && [...offered].every((k) => current.has(k)) ? new Set() : new Set(offered))),
    [offered],
  );
  const clear = useCallback(() => setSelected(new Set()), []);

  return { selected, toggle, toggleAll, clear, allSelected, size: selected.size, offered: offered.size };
}
